import React, { useMemo } from 'react';
import { AnimatedCurrency } from '../common/AnimatedNumber';

interface DividendPurchaseExamplesProps {
  fiveYearDividends: number;
}

// Purchase examples with approximate USD values
const PURCHASE_EXAMPLES = [
  { name: 'Premium Laptop', icon: '💻', value: 2000, description: 'MacBook Pro or high-end gaming laptop' },
  { name: 'Luxury Vacation', icon: '✈️', value: 5000, description: 'Week-long international trip for two' },
  { name: 'Used Car', icon: '🚗', value: 15000, description: 'Reliable pre-owned vehicle' },
  { name: 'New Car', icon: '🚙', value: 35000, description: 'Brand new mid-range sedan' },
  { name: 'House Deposit', icon: '🏠', value: 50000, description: '10-20% down payment on a home' },
  { name: 'Investment Property', icon: '🏢', value: 100000, description: 'Down payment on rental property' },
  { name: 'Early Retirement Fund', icon: '🏖️', value: 250000, description: 'Significant retirement contribution' },
  { name: 'Financial Freedom', icon: '💎', value: 500000, description: 'Life-changing wealth accumulation' }
];

/**
 * Shows relatable purchase examples based on dividend amounts
 */
export const DividendPurchaseExamples: React.FC<DividendPurchaseExamplesProps> = ({
  fiveYearDividends
}) => {
  // Find which purchases are achievable
  const achievablePurchases = useMemo(() => {
    return PURCHASE_EXAMPLES.map(item => ({
      ...item,
      isAchievable: fiveYearDividends >= item.value,
      progress: Math.min(100, (fiveYearDividends / item.value) * 100),
      remaining: Math.max(0, item.value - fiveYearDividends)
    }));
  }, [fiveYearDividends]);

  // Find the highest achievable purchase
  const highestAchievable = useMemo(() => {
    const achievable = achievablePurchases.filter(p => p.isAchievable);
    return achievable.length > 0 ? achievable[achievable.length - 1] : null;
  }, [achievablePurchases]);

  // Find the next goal
  const nextGoal = useMemo(() => {
    return achievablePurchases.find(p => !p.isAchievable);
  }, [achievablePurchases]);

  return (
    <div className="dividend-purchase-examples">
      <div className="examples-header">
        <h3>🛒 What Could Your Estimated Dividends Buy?</h3>
        <p>Based on projected mining yield — actual dividends depend on gold production &amp; market conditions</p>
      </div>

      {/* Achievement highlight */}
      {highestAchievable && (
        <div className="achievement-highlight">
          <div className="achievement-icon">{highestAchievable.icon}</div>
          <div className="achievement-content">
            <div className="achievement-label">Your estimated dividends could buy:</div>
            <div className="achievement-name">{highestAchievable.name}</div>
            <div className="achievement-description">{highestAchievable.description}</div>
          </div>
          <div className="achievement-badge">✓ Achievable</div>
        </div>
      )}

      {/* Purchase grid */}
      <div className="purchase-grid">
        {achievablePurchases.map((item, index) => (
          <div 
            key={item.name}
            className={`purchase-card ${item.isAchievable ? 'achievable' : 'not-achievable'}`}
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            <div className="purchase-icon">{item.icon}</div>
            <div className="purchase-name">{item.name}</div>
            <div className="purchase-value">
              <AnimatedCurrency value={item.value} />
            </div>
            
            {/* Progress bar */}
            <div className="purchase-progress">
              <div 
                className="progress-fill"
                style={{ width: `${item.progress}%` }}
              />
            </div>
            
            <div className="purchase-status">
              {item.isAchievable ? (
                <span className="status-achieved">✓ Covered</span>
              ) : (
                <span className="status-remaining">
                  <AnimatedCurrency value={item.remaining} /> more needed
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Next goal callout */}
      {nextGoal && (
        <div className="next-goal-callout">
          <div className="goal-icon">🎯</div>
          <div className="goal-content">
            <strong>Next Goal:</strong> Add more shares to reach{' '}
            <AnimatedCurrency value={nextGoal.value} /> for a {nextGoal.name}!
            <span className="goal-remaining">
              Only <AnimatedCurrency value={nextGoal.remaining} /> away
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DividendPurchaseExamples;

